import type { FilterableProductType } from '@/helpers/products-filter-type'
import {
  DEFAULT_PRODUCTS_PAGE,
  isProductsSearchSort,
  type ProductsSearchQuery,
  type ProductsSearchSort,
} from '@/http/zamply/products/search-products-schema'

export const CATALOG_PAGE_SIZE = 18
export const DEFAULT_CATALOG_SORT: ProductsSearchSort = 'most_relevant'

/** Slug de tipo que não é uma área real — não deve virar filtro `areas[]`. */
export const WEBINARS_AREA_SLUG = 'webinars'

/** Nomes dos query params da URL de `/cursos` (voltados ao usuário). */
export const CATALOG_URL_PARAMS = {
  search: 'q',
  workload: 'c',
  page: 'page',
  sort: 'sort',
  videoClass: 'hash_video',
} as const

/** Subconjunto de `URLSearchParams` compartilhado com `ReadonlyURLSearchParams`. */
export interface SearchParamsReader {
  get(name: string): string | null
}

export type RawSearchParams = Record<string, string | string[] | undefined>

/** Converte o `searchParams` recebido por uma página server em `URLSearchParams`. */
export function toURLSearchParams(raw: RawSearchParams): URLSearchParams {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(raw)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, item)
      }
    } else if (value !== undefined) {
      searchParams.append(key, value)
    }
  }
  return searchParams
}

function readTrimmed(searchParams: SearchParamsReader, key: string) {
  const value = searchParams.get(key)?.trim()
  return value ? value : undefined
}

function parsePage(value: string | undefined) {
  const page = Number(value)
  return Number.isInteger(page) && page > 0 ? page : DEFAULT_PRODUCTS_PAGE
}

function parseSort(value: string | undefined): ProductsSearchSort {
  return isProductsSearchSort(value) ? value : DEFAULT_CATALOG_SORT
}

function parseWorkload(value: string | undefined) {
  const workload = Number(value)
  return Number.isFinite(workload) && workload > 0 ? [workload] : undefined
}

function parseVideoClass(value: string | undefined) {
  return value === 'true' || value === '1' ? true : undefined
}

export interface BuildCatalogQueryInput {
  searchParams: SearchParamsReader
  productType: FilterableProductType
  areaSlug?: string
  categorySlug?: string
}

/**
 * Monta a query do BFF a partir da URL de `/cursos`. Usada no servidor
 * (prefetch) e no cliente (`useSearchParams`), por isso precisa ser pura e
 * determinística — o resultado é a query key do TanStack Query.
 */
export function buildCatalogQuery({
  searchParams,
  productType,
  areaSlug = '',
  categorySlug = '',
}: BuildCatalogQueryInput): ProductsSearchQuery {
  const isRealArea = areaSlug !== '' && areaSlug !== WEBINARS_AREA_SLUG

  return {
    page: parsePage(readTrimmed(searchParams, CATALOG_URL_PARAMS.page)),
    per_page: CATALOG_PAGE_SIZE,
    search: readTrimmed(searchParams, CATALOG_URL_PARAMS.search),
    sort: parseSort(readTrimmed(searchParams, CATALOG_URL_PARAMS.sort)),
    product_types: [productType],
    areas: isRealArea ? [areaSlug] : undefined,
    categories: categorySlug ? [categorySlug] : undefined,
    workload_values: parseWorkload(
      readTrimmed(searchParams, CATALOG_URL_PARAMS.workload)
    ),
    has_videoclass: parseVideoClass(
      readTrimmed(searchParams, CATALOG_URL_PARAMS.videoClass)
    ),
  }
}

/**
 * `links.area_highlighted_product` é uma URL/path REST terminando na
 * referência do produto; pega o último segmento do caminho.
 */
export function extractProductReference(
  link: string | null | undefined
): string | null {
  if (!link) {
    return null
  }
  const path = link.split('?').at(0) ?? ''
  const segment = path.split('/').filter(Boolean).at(-1)
  return segment ?? null
}
